<template>
  <div class="home">
    <div class="input-container">
      <header>
        <router-link :to="{ name: 'home' }">
          <div class="header-sticker">
            STICKERS
          </div>
        </router-link>
      </header>
      <input placeholder="SEARCH" v-model="query" @keyup="searchInputChanged"/>
      <div class="navigation">
        <span v-if="showWithoutText">
          you're looking at stickers without any text on them
        </span>
        <router-link :to="{ name: 'without_text' }" v-if="!showWithoutText">
          see stickers without any text on them
        </router-link>
      </div>
      <div class="navigation">
        <span v-if="!query && !showWithoutText">
          you're looking at all stickers
        </span>
        <router-link :to="{ name: 'home' }" v-if="showWithoutText || query">
          see all stickers
        </router-link>
      </div>
      <div class="navigation related">
        <a href="https://mwarning.github.io/chaos-sticker-collection/" target="_blank" rel="noopener">[1]</a>
        <span class="related-label">&larr; related work &rarr;</span>
        <a href="https://stickertop.art/" target="_blank" rel="noopener">[2]</a>
      </div>
    </div>
    <StickersComponent :stickers="stickers"></StickersComponent>
    <div class="no-stickers" v-if="stickers.length == 0">
      there are no stickers that match this query
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { stickers, Sticker } from '@/domain/Stickers';

import StickersComponent from '@/components/Stickers.vue';

export default defineComponent({
  name: 'HomeView',
  components: {
    StickersComponent,
  },
  data() {
    return {
      query: '',
      showWithoutText: false,
    };
  },
  mounted(): void {
    this.copyDataFromLink();
  },
  watch: {
    $route(): void {
      this.copyDataFromLink();
    },
  },
  computed: {
    stickers(): Sticker[] {
      const reversedStickers = [...stickers].reverse();

      if (this.showWithoutText) {
        return reversedStickers.filter((v) => v.text.length === 0);
      }

      if (this.query === '') {
        return reversedStickers;
      }

      return reversedStickers.filter((v) => v.text.toLowerCase().includes(this.query.toLowerCase()));
    },
  },
  methods: {
    searchInputChanged(): void {
      if (this.query === '') {
        this.$router.replace({ name: 'home' });
      } else {
        this.$router.replace({ name: 'with_text', params: { query: this.query } });
      }
    },
    copyDataFromLink(): void {
      const query = this.$route.params.query;
      if (query) {
        this.query = query as string;
      } else {
        this.query = '';
      }

      const showWithoutText = this.$route.name === 'without_text';
      if (showWithoutText) {
        this.showWithoutText = true;
      } else {
        this.showWithoutText = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
  $margin: 5em;

  header {
    .header-sticker {
      margin-bottom: 1em;
      padding: 0.1em 1em;
      font-size: 100px;
      display: inline-block;
      background-color: var(--cyber-yellow);
      color: var(--blackest-black);
      font-weight: bold;
    }
  }

  .input-container {
    margin: $margin 0;

    input {
      border: 1px solid var(--cyber-yellow);
      background-color: transparent;
      font-size: 30px;
      padding: .5em;
      color: var(--cyber-yellow);
      font-family: inherit;
      text-align: center;
      margin-bottom: 1em;

      &:focus {
        outline: none;
      }
    }

    .navigation {
      color: var(--whitest-white);
      padding: 1em;

      a {
        color: var(--cyber-yellow);
      }
    }

    .related {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1em;

      .related-label {
        color: var(--whitest-white);
      }
    }
  }

  @media (max-width: 1000px) {
    header {
      .header-sticker {
        font-size: 75px;
      }
    }

    .input-container {
      input {
        font-size: 25px;
      }
    }
  }

  @media (max-width: 750px) {
    header {
      .header-sticker {
        font-size: 50px;
      }
    }

    .input-container {
      input {
        font-size: 20px;
      }
    }
  }

  .no-stickers {
    color: var(--whitest-white);

    a {
      color: var(--cyber-yellow);

    }
  }
</style>
